import { useEffect, useMemo } from "react";
import { DatePicker, Form, Input, Select, Switch } from "antd";
import { type FormInstance } from "antd/lib/form";
import { useTranslation } from "react-i18next";

import { CheckoutVariantTarget, type CheckoutVariant } from "@fitness-app/data-models/entities/CheckoutVariant";

import { useProductPricesOptions } from "~/modules/Products/hooks/useProductPricesOptions";
import { type ProductCheckoutVariantFormModel } from "./types";

interface ProductCheckoutVariantFormProps {
  formController?: FormInstance<ProductCheckoutVariantFormModel>;
  onSubmit: (formData: ProductCheckoutVariantFormModel) => void;
  model?: Partial<ProductCheckoutVariantFormModel> | null;
  domain?: string;
  variants?: CheckoutVariant[];
}

const ProductCheckoutVariantForm = ({
  model,
  onSubmit,
  formController,
  domain,
  variants,
}: ProductCheckoutVariantFormProps) => {
  const { t } = useTranslation(["products", "common"]);
  const options = useProductPricesOptions(undefined, true);
  const target = Form.useWatch("target", formController);
  const selectedPrices = Form.useWatch("prices", formController) || [];
  const selectedDownsellPrices = Form.useWatch("downsellPrices", formController) || [];
  const allPricesManual = Form.useWatch(["metadata", "allowManualPayment"], formController) ?? false;

  useEffect(() => {
    if (model) {
      formController?.setFieldsValue({
        ...model,
        downsellPrices: model.downsellPrices ?? [],
      });
    }
  }, [formController, model]);

  const pathPrefix = target === CheckoutVariantTarget.Renewal ? "przedluzenie" : "step";

  const availablePricesOptions = useMemo(
    () =>
      options.map((option) => ({
        ...option,
        disabled: selectedDownsellPrices.includes(String(option.value)),
      })),
    [options, selectedDownsellPrices],
  );

  const downsellPricesOptions = useMemo(
    () =>
      options.map((option) => ({
        ...option,
        disabled: selectedPrices.includes(String(option.value)),
      })),
    [options, selectedPrices],
  );

  const manualPaymentPricesOptions = useMemo(
    () =>
      options.filter(
        (option) =>
          selectedPrices.includes(String(option.value)) || selectedDownsellPrices.includes(String(option.value)),
      ),
    [options, selectedPrices, selectedDownsellPrices],
  );

  const renewalVariantsOptions = useMemo(
    () =>
      variants
        ?.filter((variant) => variant.target === CheckoutVariantTarget.Renewal)
        .map((variant) => ({
          label: variant.slug,
          value: variant.id,
        })),
    [variants],
  );

  return (
    <Form<ProductCheckoutVariantFormModel>
      name="form"
      layout="vertical"
      form={formController}
      initialValues={{
        target: CheckoutVariantTarget.New,
        slug: "",
        isActive: true,
        prices: [],
        downsellPrices: [],
        default: false,
        activeUntil: null,
        metadata: { allowManualPayment: false, manualPaymentPrices: [] },
      }}
      onFinish={onSubmit}
    >
      <Form.Item
        label="Slug"
        rules={[
          {
            required: true,
            message: t<string>("common:validationErrors.fieldIsRequired"),
          },
          {
            min: 1,
            message: t<string>("common:validationErrors.minLength", {
              number: 1,
            }),
          },
        ]}
        name="slug"
      >
        <Input
          autoFocus
          className="max-w-md"
          addonBefore={
            domain
              ? `${domain}.${import.meta.env.VITE_ECOMMERCE_DOMAIN}/${pathPrefix}/`
              : `${import.meta.env.VITE_ECOMMERCE_DOMAIN}/${pathPrefix}/`
          }
        />
      </Form.Item>

      <Form.Item
        label="Typ koszyka"
        name="target"
        rules={[
          {
            required: true,
            message: t<string>("common:validationErrors.fieldIsRequired"),
          },
        ]}
      >
        <Select
          className="max-w-xs"
          options={[
            {
              label: "Tylko nowi klienci",
              value: CheckoutVariantTarget.New,
            },
            {
              label: "Tylko aktualni klienci",
              value: CheckoutVariantTarget.Renewal,
            },
          ]}
        />
      </Form.Item>

      <Form.Item name="isActive" label="Aktywny" valuePropName="checked">
        <Switch />
      </Form.Item>

      <Form.Item name="default" label="Domyślny wariant" valuePropName="checked">
        <Switch />
      </Form.Item>

      <Form.Item
        label={t("prices.priceTime")}
        name="activeUntil"
        tooltip={t("prices.willBeShowsTimerInCart")}
        rules={[
          {
            required: false,
            message: t<string>("common:validationErrors.fieldIsRequired"),
          },
          {
            validator: (_, value) =>
              !value || value > Date.now()
                ? Promise.resolve()
                : Promise.reject(new Error(t<string>("prices.cannotBeEarlierThanNow"))),
          },
        ]}
      >
        <DatePicker
          showTime
          format="YYYY-MM-DD | HH:mm"
          disabledDate={(current) => current && current.valueOf() < Date.now() - 60 * 60 * 24}
        />
      </Form.Item>

      <Form.Item
        label="Dostępne ceny"
        name="prices"
        dependencies={["downsellPrices"]}
        rules={[
          {
            required: true,
            message: t<string>("common:validationErrors.fieldIsRequired"),
          },
          ({ getFieldValue }) => ({
            validator: (_, value: string[] | undefined) => {
              const downsellPrices = (getFieldValue("downsellPrices") || []) as string[];
              const hasOverlap = (value || []).some((priceId) => downsellPrices.includes(priceId));

              return hasOverlap
                ? Promise.reject(new Error("Cena nie może być jednocześnie podstawowa i downsellowa."))
                : Promise.resolve();
            },
          }),
        ]}
      >
        <Select
          className="max-w-lg"
          mode="multiple"
          showSearch
          optionFilterProp="label"
          placeholder="Wybierz ceny, które mają być dostępne w koszyku"
          popupMatchSelectWidth={false}
          options={availablePricesOptions}
        />
      </Form.Item>

      <Form.Item
        label="Ceny downsellowe"
        name="downsellPrices"
        dependencies={["prices"]}
        tooltip="Opcjonalne ceny pokazywane jako oferta downsell dla tego wariantu."
        rules={[
          ({ getFieldValue }) => ({
            validator: (_, value: string[] | undefined) => {
              const regularPrices = (getFieldValue("prices") || []) as string[];
              const hasOverlap = (value || []).some((priceId) => regularPrices.includes(priceId));

              return hasOverlap
                ? Promise.reject(new Error("Cena nie może być jednocześnie podstawowa i downsellowa."))
                : Promise.resolve();
            },
          }),
        ]}
      >
        <Select
          allowClear
          className="max-w-lg"
          mode="multiple"
          showSearch
          optionFilterProp="label"
          placeholder="Wybierz ceny pokazywane w downsellu"
          popupMatchSelectWidth={false}
          options={downsellPricesOptions}
        />
      </Form.Item>

      {renewalVariantsOptions?.length ? (
        <Form.Item<ProductCheckoutVariantFormModel> shouldUpdate={(prev, current) => prev.target !== current.target}>
          {({ getFieldValue }) => {
            const target = getFieldValue("target") as ProductCheckoutVariantFormModel["target"];
            if (target === CheckoutVariantTarget.New) {
              return (
                <Form.Item
                  label="Wariant, do którego mają być przekierowani klienci, którzy już posiadają dostęp do produktu"
                  name={["metadata", "renewalFallback"]}
                >
                  <Select allowClear className="max-w-md" options={renewalVariantsOptions} />
                </Form.Item>
              );
            }
            return null;
          }}
        </Form.Item>
      ) : null}

      <Form.Item
        name={["metadata", "allowManualPayment"]}
        label="Wszystkie ceny wariantu mogą być płacone manualnie"
        valuePropName="checked"
      >
        <Switch />
      </Form.Item>

      <Form.Item
        name={["metadata", "manualPaymentPrices"]}
        label="Ceny, które mogą być płacone manualnie"
        tooltip="Przelew tradycyjny tylko przy wybranych cenach tego wariantu."
        hidden={allPricesManual}
      >
        <Select
          allowClear
          className="max-w-lg"
          mode="multiple"
          showSearch
          optionFilterProp="label"
          placeholder="Brak, płatność manualna wyłączona"
          popupMatchSelectWidth={false}
          options={manualPaymentPricesOptions}
        />
      </Form.Item>
    </Form>
  );
};

export default ProductCheckoutVariantForm;
